// This file's CardGrid and profile section have been refactored into reusable components:
// - src/components/UserProfileCard.jsx
// - src/components/CardGrid.jsx
// Please use those components instead.

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UserProfileCard from '../components/UserProfileCard';
import CardGrid from './CardGrid';
import ProviderMenu from '../pages/login/providerMenu';
import AllergyCard from '../pageComponents/widgets/allergies';
import { UsersAllergiesGet, getMedicationatDashboard, getLabResultatDashboard, UsersAppointmentsGet, getVitals } from '../apiServices/reducer/apiService';

const profile = {
  imageUrl: 'https://picsum.photos/seed/profile/60/60',
  name: 'CHRISTOPHER HENRY ',
  phone: '(555) 123-4567',
  joined: 'DECEMBER 20, 2024',
};

const banner = {
  status: 'Covid 19',
  result: 'Negative',
  lastUpdated: '05/09/2025',
};

const PatientSummarySection = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.apiService?.token);
  const [allergiesList, setAllergiesList] = useState([]);
  const [medicationsList, setMedicationsList] = useState([]);
  const [labResultsList, setLabResultsList] = useState([]);
  const [appointmentsList, setAppointmentsList] = useState([]);
  const [vitalsList, setVitalsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [medicationsLoading, setMedicationsLoading] = useState(true);
  const [labResultsLoading, setLabResultsLoading] = useState(true);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [vitalsLoading, setVitalsLoading] = useState(true);

  // Get patient data from localStorage
  const patientUserId = localStorage.getItem('patientUserId');
  const patientUserName = localStorage.getItem('patientUserName');

  // Update profile name if patient data exists
  if (patientUserName) {
    profile.name = patientUserName.toUpperCase();
  }

  const fetchAllergies = async () => {
    if (!patientUserId || !token) {
      setLoading(false);
      return;
    }

    try {
      const response = await dispatch(UsersAllergiesGet({
        UserID: patientUserId,
        token,
        _cache: Date.now()
      })).unwrap();

      console.log('Allergies response:', response);

      if (response && Array.isArray(response)) {
        const activeAllergies = response.filter(item => item.is_active !== false);
        setAllergiesList(activeAllergies);
      } else {
        setAllergiesList([]);
      }
    } catch (err) {
      console.error('Error fetching allergies:', err);
      setAllergiesList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedications = async () => {
    if (!patientUserId || !token) {
      setMedicationsLoading(false);
      return;
    }

    try {
      const response = await dispatch(getMedicationatDashboard({
        token: token,
        user_id: patientUserId
      })).unwrap();

      console.log('Medications response:', response);

      if (response && response.Table && Array.isArray(response.Table)) {
        const activeMedications = response.Table.filter(item =>
          item.medication_status !== 'Inactive' &&
          item.medication_status !== 'Discontinued'
        );
        setMedicationsList(activeMedications);
      } else {
        setMedicationsList([]);
      }
    } catch (err) {
      console.error('Error fetching medications:', err);
      setMedicationsList([]);
    } finally {
      setMedicationsLoading(false);
    }
  };

  const fetchLabResults = async () => {
    if (!patientUserId || !token) {
      setLabResultsLoading(false);
      return;
    }

    try {
      const response = await dispatch(getLabResultatDashboard({
        user_id: patientUserId,
        token: token
      })).unwrap();

      console.log('Lab Results response:', response);

      let labResultData = [];
      if (response && Array.isArray(response)) {
        labResultData = response;
      } else if (response && response.Table && Array.isArray(response.Table)) {
        labResultData = response.Table;
      } else if (response && response.data && response.data.Table && Array.isArray(response.data.Table)) {
        labResultData = response.data.Table;
      }

      if (labResultData.length > 0) {
        // Take only the most recent 5 lab results
        const recentLabResults = labResultData.slice(0, 5);
        setLabResultsList(recentLabResults);
      } else {
        setLabResultsList([]);
      }
    } catch (err) {
      console.error('Error fetching lab results:', err);
      setLabResultsList([]);
    } finally {
      setLabResultsLoading(false);
    }
  };

  const fetchAppointments = async () => {
    if (!patientUserId || !token) {
      setAppointmentsLoading(false);
      return;
    }

    try {
      const response = await dispatch(UsersAppointmentsGet({
        UserID: patientUserId,
        token
      })).unwrap();

      console.log('Appointments response:', response);

      if (response && Array.isArray(response)) {
        // Take only the most recent 5 appointments
        const recentAppointments = response.slice(0, 5);
        setAppointmentsList(recentAppointments);
      } else {
        setAppointmentsList([]);
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setAppointmentsList([]);
    } finally {
      setAppointmentsLoading(false);
    }
  };

  const fetchVitals = async () => {
    if (!patientUserId || !token) {
      setVitalsLoading(false);
      return;
    }

    try {
      const response = await dispatch(getVitals({
        user_id: patientUserId,
        token: token
      })).unwrap();

      console.log('Vitals response:', response);

      if (response && Array.isArray(response)) {
        // Take only the most recent 5 vitals records
        const recentVitals = response.slice(0, 5);
        setVitalsList(recentVitals);
      } else {
        setVitalsList([]);
      }
    } catch (err) {
      console.error('Error fetching vitals:', err);
      setVitalsList([]);
    } finally {
      setVitalsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllergies();
    fetchMedications();
    fetchLabResults();
    fetchAppointments();
    fetchVitals();
  }, [patientUserId, token]);

  // Create dynamic card data with allergies, medications, lab results, appointments, and vitals
  const cardData = [
    {
      id: 1,
      title: 'Active Problems',
      text: `• Asthma\n• Allergic rhinitis\n• Obesity\n• Hyperlipidemia\n• Constipation\n• Other diagnoses with ICD-10 codes`,
    },
    {
      id: 2,
      title: 'Allergies / Reactions',
      text: loading ? 'Loading...' :
        allergiesList.length > 0 ?
          allergiesList.map(item =>
            `• ${item.allergy_name || 'N/A'}: ${item.reactions || 'N/A'}`
          ).join('\n') :
          'No allergies recorded',
    },
    {
      id: 3,
      title: 'COVID-19 Status',
      text: `• Result: Negative`,
    },
    {
      id: 4,
      title: 'Postings',
      text: `• Research Enrollment Note - Jul 26, 2023\n• Advance Directive Discussion - Mar 15, 2023`,
    },
    {
      id: 5,
      title: "Women's Health",
      text: `• Not Applicable`,
    },
    {
      id: 6,
      title: 'Active Medications',
      text: medicationsLoading ? 'Loading...' :
        medicationsList.length > 0 ?
          medicationsList.map(item =>
            `• ${item.medication_name || item.Medication || 'N/A'} - ${item.medication_dose || item.dose || 'N/A'} (${item.medication_recurrence || item.frequency || 'N/A'})`
          ).join('\n') :
          'No active medications recorded',
    },
    {
      id: 7,
      title: 'Recent Lab Results',
      text: labResultsLoading ? 'Loading...' :
        labResultsList.length > 0 ?
          labResultsList.map(item =>
            `• ${item.report_name || 'N/A'}: ${item.report_date || 'N/A'}`
          ).join('\n') :
          'No lab results available',
    },
    {
      id: 8,
      title: 'Clinical Reminders',
      text: `• Advance Directive\n• Pulmonary Function\n• Immunizations\n• Due dates: Dec 2022 - Dec 2023`,
    },
    {
      id: 9,
      title: 'Vitals',
      text: vitalsLoading ? 'Loading...' :
        vitalsList.length > 0 ?
          vitalsList.map(item =>
            `• ${item.LastUpdated || 'N/A'}: BP ${item.BloodPressure_Systolic || 'N/A'}/${item.BloodPressure_Diastolic || 'N/A'}, HR ${item.HeartRate || 'N/A'}, Weight ${item.Weight || 'N/A'} lbs`
          ).join('\n') :
          'No vitals data available',
    },
    {
      id: 10,
      title: 'Appointments & Notes',
      text: appointmentsLoading ? 'Loading...' :
        appointmentsList.length > 0 ?
          appointmentsList.map(item =>
            `• ${item.location_name || 'N/A'} - ${item.appt_date || 'N/A'} at ${item.appt_start || 'N/A'} to ${item.appt_end || 'N/A'}`
          ).join('\n') :
          'No upcoming appointments',
    },
  ];

  return (
    <>
      <UserProfileCard profile={profile} banner={banner} />
      <div className="patient-summary-content">
        <CardGrid cardData={cardData} />
        <div className="provider-menu-spacer"></div>
        <ProviderMenu />
      </div>
    </>
  );
};

export default PatientSummarySection;
